import React, { memo, useMemo } from 'react';
import { List } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { SmartDiaryEntry } from '../type';
import { SmartListItem } from './SmartListItem';

type SmartDiaryListProps = { smartEntries: SmartDiaryEntry[] };

export const usePushUncategorisedToEnd = (
  smartEntries: SmartDiaryEntry[]
): SmartDiaryEntry[] =>
  useMemo<SmartDiaryEntry[]>(() => {
    const { categorizedEntries, uncategorisedEntries } = smartEntries.reduce(
      (acc, smartEntry) => {
        if (!smartEntry.client && !smartEntry.project && !smartEntry.task) {
          acc.uncategorisedEntries.push(smartEntry);
        } else {
          acc.categorizedEntries.push(smartEntry);
        }
        return acc;
      },
      {
        categorizedEntries: [],
        uncategorisedEntries: []
      } as {
        categorizedEntries: SmartDiaryEntry[];
        uncategorisedEntries: SmartDiaryEntry[];
      }
    );
    return [...categorizedEntries, ...uncategorisedEntries];
  }, [smartEntries]);

const useListStyles = makeStyles((theme) => ({
  root: {
    marginRight: theme.spacing(2)
  },
  padding: {
    padding: 0
  }
}));

export const SmartDiaryList: React.FC<SmartDiaryListProps> = memo(
  ({ smartEntries }: SmartDiaryListProps) => {
    const orderedSmartEntries = usePushUncategorisedToEnd(smartEntries);

    const items = useMemo(
      () =>
        orderedSmartEntries.map((smartEntry) => (
          <SmartListItem key={smartEntry.id} smartEntry={smartEntry} />
        )),
      [orderedSmartEntries]
    );

    return (
      <List classes={useListStyles()} data-testid="smart-diary-list">
        {items}
      </List>
    );
  }
);

SmartDiaryList.displayName = 'SmartDiaryList';
